<template>
  <div class="opportunity-forms-page">
    <OpportunityHeader />
    <BaseLoadingBar v-if="opportunityPending" />

    <div v-else class="opportunity-forms-page__content">
      <input
        ref="fileInput"
        type="file"
        accept=".pdf"
        multiple
        class="opportunity-forms-page__hidden-input"
        @change="onFileSelected"
      />

      <div v-if="templates.length" class="opportunity-forms-page__header">
        <div class="opportunity-forms-page__intro">
          <h1 class="opportunity-forms-page__title">{{ $t('page.opportunity-forms.title') }}</h1>
          <p class="opportunity-forms-page__stat">
            <template v-if="forms.length">
              {{
                $tc('page.opportunity-forms.forms-identified', forms.length, {
                  count: forms.length,
                  files: filesLabel,
                })
              }}
            </template>
            <span v-else>{{ $t('page.opportunity-forms.subtitle') }}</span>
          </p>
        </div>
        <div class="opportunity-forms-page__actions">
          <FileUpload
            minimalist
            multiple
            allowed-files=".pdf"
            class="opportunity-forms-page__add"
            @add-files="onFilesAdded"
          />
          <BaseButton is-outlined has-icon-left @click.native="onDownloadAll">
            <BaseIcon name="download" />
            {{ $t('page.opportunity-forms.download-all') }}
          </BaseButton>
          <FillSplitButton
            :label="$t('page.opportunity-forms.fill-all')"
            :templates="orderedTemplates"
            :default-template-id="scopedTemplateId"
            :disabled="!forms.length || anyFilling"
            @fill="onFillAll"
          />
        </div>
      </div>

      <div v-if="!templates.length && !opportunityPending" class="opportunity-forms-page__empty">
        <LargeNotice
          v-on:click="goToTemplates"
          :title="$t('page.opportunity-forms.no-template-title')"
          :subtitle="$t('page.opportunity-forms.no-template-subtitle')"
          :buttonText="$t('page.opportunity-forms.configure-templates')"
          buttonIcon="arrow-right"
        />
      </div>

      <div v-else-if="!forms.length && !opportunityPending" class="opportunity-forms-page__empty">
        <LargeNotice
          v-on:click="triggerUpload"
          :title="$t('page.opportunity-forms.empty-title')"
          :subtitle="$t('page.opportunity-forms.empty-subtitle')"
          :buttonText="$t('page.opportunity-forms.upload')"
          buttonIcon="upload"
        />
      </div>

      <template v-else>
        <div class="opportunity-forms-page__controls">
          <div v-if="templates.length" class="opportunity-forms-page__scope">
            <span class="opportunity-forms-page__scope-label">{{ $t('page.opportunity-forms.profile') }}</span>
            <!-- On phones, in a sheet (docs/page-layout.md). -->
            <v-menu offset-y transition="scale-transition" min-width="280" :disabled="isPhone">
              <template v-slot:activator="{ on, attrs }">
                <button class="opportunity-forms-page__scope-current" v-on="on" v-bind="attrs" @click="openScopeSheet">
                  <BaseIcon name="file-text" class="opportunity-forms-page__scope-icon" />
                  <span v-if="scopedTemplate" class="opportunity-forms-page__scope-name">
                    {{ scopedTemplate.name }}
                  </span>
                  <BaseIcon name="chevron-down" class="opportunity-forms-page__scope-caret" />
                </button>
              </template>
              <div class="opportunity-forms-page__scope-menu">
                <div class="opportunity-forms-page__scope-menu-heading">
                  {{ $t('page.opportunity-forms.switch-template') }}
                </div>
                <button
                  v-for="t in orderedTemplates"
                  :key="t.id"
                  type="button"
                  class="opportunity-forms-page__scope-option"
                  :class="{ 'is-active': t.id === scopedTemplateId }"
                  @click="onScopeChange(t)"
                >
                  <span class="opportunity-forms-page__scope-option-name">{{ t.name }}</span>
                  <span class="opportunity-forms-page__scope-option-meta">
                    <BaseTag v-if="t.isDefault" preset="soft" tone="success">
                      {{ $t('page.opportunity-forms.default') }}
                    </BaseTag>
                  </span>
                </button>
              </div>
            </v-menu>
            <BaseBottomSheet
              v-if="isPhone"
              v-model="scopeSheetOpen"
              :title="$t('page.opportunity-forms.switch-template')"
            >
              <ul class="base-bottom-sheet__menu" role="radiogroup">
                <li v-for="t in orderedTemplates" :key="t.id">
                  <button
                    type="button"
                    role="radio"
                    :aria-checked="String(t.id === scopedTemplateId)"
                    :class="{ 'is-active': t.id === scopedTemplateId }"
                    @click="onScopeSheetChange(t)"
                  >
                    {{ t.name }}
                    <BaseTag v-if="t.isDefault" preset="soft" tone="success">
                      {{ $t('page.opportunity-forms.default') }}
                    </BaseTag>
                    <BaseIcon v-if="t.id === scopedTemplateId" name="check" class="base-bottom-sheet__menu-check" />
                  </button>
                </li>
              </ul>
            </BaseBottomSheet>
          </div>
          <span v-if="templates.length" class="opportunity-forms-page__divider" />
          <div class="opportunity-forms-page__controls-left">
            <BaseSearchBarNew
              class="opportunity-forms-page__search"
              name="formsSearch"
              is-grey
              has-debounce
              :value="search"
              :placeholder="$t('page.opportunity-forms.search-placeholder')"
              @input="onSearch"
            />
            <div class="opportunity-forms-page__filters">
              <button
                v-for="option in statusFilters"
                :key="option"
                class="opportunity-forms-page__filter"
                :class="{ 'is-active': statusFilter === option }"
                @click="statusFilter = option"
              >
                {{ $t(`page.opportunity-forms.filter.${option.toLowerCase()}`) }}
              </button>
            </div>
          </div>
        </div>

        <FillProgress
          v-if="anyFilling"
          class="opportunity-forms-page__fill-progress"
          :completed="fillSession ? fillSession.completed : 0"
          :total="fillSession ? fillSession.total : 0"
          :step="fillStep"
        />

        <FormsList
          v-model="selectedForms"
          :forms="visibleForms"
          :templates="orderedTemplates"
          :default-template-id="scopedTemplateId"
          :any-filling="anyFilling"
          @fill="onFillOne"
          @review="onReview"
          @download="onDownloadOne"
          @fill-selected="onFillSelected"
          @download-selected="onDownloadSelected"
          @delete-selected="onConfirmDeleteForms"
          @delete-one="onConfirmDeleteForm"
        />
      </template>
    </div>

    <DeleteDialog ref="deleteFormsDialog" @execute="onDeleteForms" :buttonText="$t('actions.delete')">
      <template slot="content">
        <div class="opportunity-forms-page__delete">
          {{
            $tc('page.opportunity-forms.delete-dialog.confirm', selectedForms.length, { count: selectedForms.length })
          }}
        </div>
      </template>
    </DeleteDialog>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import BaseSearchBarNew from '@/components/base/BaseSearchBarNew';
import BaseTag from '@/components/base/BaseTag.vue';
import LargeNotice from '@/components/notice/LargeNotice.vue';
import DeleteDialog from '@/components/page-admin-keywords/DeleteDialog.vue';
import OpportunityHeader from '@/components/page-opportunities/OpportunityHeader';
import FormsList from '@/components/page-opportunity-forms/FormsList.vue';
import FillSplitButton from '@/components/page-opportunity-forms/FillSplitButton.vue';
import FillProgress from '@/components/page-opportunity-forms/FillProgress.vue';
import FileUpload from '@/components/form/FileUpload';

import { paths } from '@/util/paths';
import { opportunityDocumentsService } from '@/services/opportunity-documents.service';
import { downloadBlob } from '@/util/download-file';
import { mapActions, mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';

// Display order for the forms list: filled → partially filled → not filled → error.
const STATUS_ORDER = { FILLED: 0, PARTIALLY_FILLED: 1, NOT_FILLED: 2, ERROR: 3 };
const statusRank = (status) => (status in STATUS_ORDER ? STATUS_ORDER[status] : 99);

export default {
  name: 'PageOpportunityForms',
  components: {
    BaseBottomSheet,
    BaseButton,
    BaseIcon,
    BaseLoadingBar,
    BaseSearchBarNew,
    BaseTag,
    LargeNotice,
    DeleteDialog,
    OpportunityHeader,
    FormsList,
    FillSplitButton,
    FillProgress,
    FileUpload,
  },
  props: {
    opportunityId: String,
  },
  data() {
    return {
      scopeSheetOpen: false,
      selectedForms: [],
      previousStatuses: {},
      search: '',
      statusFilter: 'all',
      statusFilters: ['all', 'NOT_FILLED', 'PARTIALLY_FILLED', 'FILLED'],
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('opportunity', { opportunityPending: 'pending' }),
    ...mapGetters('formFill', [
      'anyFillingFor',
      'isFilling',
      'opportunityTemplate',
      'formFillByDocument',
      'fillSessionFor',
      'fillStep',
    ]),
    anyFilling() {
      return this.anyFillingFor(this.opportunityId);
    },
    ...mapGetters('tenantProfile', ['templates']),
    // Forms are the opportunity's FORM-type documents, merged with their
    // form-fill record (status + field counts) when one exists.
    forms() {
      const documents = (this.opportunity && this.opportunity.documents) || [];
      return documents
        .filter((document) => document.type === 'FORM')
        .map((document) => {
          const fill = this.formFillByDocument(document.id);
          return {
            id: document.id,
            name: document.name,
            pageCount: document.pageCount,
            status: this.isFilling(document.id) ? 'FILLING' : fill && fill.status ? fill.status : 'NOT_FILLED',
            updatedAt: (fill && fill.createdAt) || document.updatedAt || document.createdAt,
            tenantProfileTemplateId: (fill && fill.tenantProfileTemplateId) || null,
          };
        });
    },
    storedScope() {
      return this.opportunityTemplate(this.opportunityId);
    },
    // The opportunity's template: the persisted choice, else the tenant's default
    // template, else the first one. Drives the picker and the fill split-button.
    scopedTemplateId() {
      if (this.storedScope && this.templates.some((t) => t.id === this.storedScope.templateId)) {
        return this.storedScope.templateId;
      }
      const fallback = this.templates.find((t) => t.isDefault) || this.templates[0];
      return fallback ? fallback.id : null;
    },
    scopedTemplate() {
      return this.templates.find((t) => t.id === this.scopedTemplateId) || null;
    },
    // The tenant's default template heads every list on this page; the rest keep their order.
    orderedTemplates() {
      return [...this.templates].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
    },
    // Total uploaded files on the opportunity (forms are the FORM-type subset).
    totalFilesCount() {
      return ((this.opportunity && this.opportunity.documents) || []).length;
    },
    // Pre-pluralized "N fichier(s)" fragment so the file noun agrees with its
    // own count, independently of the form count in forms-identified.
    filesLabel() {
      return this.$tc('page.opportunity-forms.files-count', this.totalFilesCount, { count: this.totalFilesCount });
    },
    fillingCount() {
      return this.forms.filter((f) => f.status === 'FILLING').length;
    },
    fillSession() {
      return this.fillSessionFor(this.opportunityId);
    },
    visibleForms() {
      const query = this.search.trim().toLowerCase();
      return this.forms
        .filter((form) => {
          if (this.statusFilter !== 'all' && form.status !== this.statusFilter) return false;
          return !query || form.name.toLowerCase().includes(query);
        })
        .sort((a, b) => statusRank(a.status) - statusRank(b.status));
    },
  },
  watch: {
    // Surface a toast when a form transitions to "filled" (the in-app
    // notification panel arrives in a separate PR).
    forms: {
      deep: true,
      handler(forms) {
        forms.forEach((form) => {
          const previous = this.previousStatuses[form.id];
          if (previous && previous !== 'FILLED' && form.status === 'FILLED') {
            this.$toast.success(this.$t('page.opportunity-forms.actions.fill-complete', { name: form.name }));
          }
          this.previousStatuses[form.id] = form.status;
        });
      },
    },
  },
  async created() {
    try {
      if (!this.opportunity || this.opportunity.id !== this.opportunityId) {
        await this.getOpportunity({ id: this.opportunityId });
      } else {
        // already loaded: refresh its documents in the background (a delete or
        // upload elsewhere may have changed them) without blanking the page
        this.getOpportunity({ id: this.opportunityId, silent: true }).catch(() => {});
      }
      if (!this.templates.length) {
        await this.getTemplates();
      }
      await this.getDocumentForms(this.opportunityId);
      // Ask the backend for the latest form-fill job and, if it's still
      // running, re-attach to its SSE stream — the progress state survives
      // navigation and full reloads (same pattern as watchExtractionJob).
      this.watchFillJob({ opportunityId: this.opportunityId });
      // Persist the tenant's default template as the opportunity's choice on first visit.
      if (!this.storedScope && this.scopedTemplateId) {
        this.persistScope(this.scopedTemplateId);
      }
      this.forms.forEach((form) => {
        this.previousStatuses[form.id] = form.status;
      });
    } catch (error) {
      this.handleError(error);
    }
  },
  methods: {
    ...mapActions('opportunity', ['getOpportunity']),
    ...mapActions('formFill', [
      'uploadForms',
      'startFill',
      'setOpportunityTemplate',
      'deleteForms',
      'getDocumentForms',
      'watchFillJob',
    ]),
    ...mapActions('tenantProfile', ['getTemplates']),
    persistScope(templateId) {
      this.setOpportunityTemplate({ opportunityId: this.opportunityId, templateId });
    },
    openScopeSheet() {
      if (this.isPhone) this.scopeSheetOpen = true;
    },
    // A single choice closes the sheet.
    onScopeSheetChange(template) {
      this.scopeSheetOpen = false;
      if (template.id !== this.scopedTemplateId) this.onScopeChange(template);
    },
    onScopeChange(template) {
      this.persistScope(template.id);
    },
    goToTemplates() {
      this.$router.push(paths.adminTenantProfile);
    },
    handleError(error) {
      if (!error || !error.response) {
        this.$router.replace(paths.notFound);
        return;
      }
      if (error.response.status === 404 || error.response.status === 403) {
        this.$router.replace(paths.notFound);
      } else {
        this.$toast.error(error);
      }
    },
    onSearch(value) {
      this.search = value;
    },
    triggerUpload() {
      this.$refs.fileInput.click();
    },
    onFileSelected(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      this.uploadFiles(files);
    },
    onFilesAdded(files) {
      this.uploadFiles(Array.from(files || []));
    },
    uploadFiles(files) {
      if (!files.length) return;
      this.uploadForms({ opportunityId: this.opportunityId, files })
        .then(() => {
          this.$toast.success(
            this.$tc('page.opportunity-forms.actions.upload.success', files.length, { count: files.length }),
          );
        })
        .catch(() => this.$toast.error(this.$t('page.opportunity-forms.actions.upload.fail')));
    },
    startFillFor(formIds, templateId) {
      if (!formIds.length) return;
      this.startFill({ formIds, templateId, opportunityId: this.opportunityId });
      this.$toast.info(
        this.$tc('page.opportunity-forms.actions.fill-started', formIds.length, { count: formIds.length }),
      );
    },
    onFillOne({ formId, templateId }) {
      this.startFillFor([formId], templateId || this.scopedTemplateId);
    },
    onFillAll(templateId) {
      this.startFillFor(
        this.forms.map((f) => f.id),
        templateId || this.scopedTemplateId,
      );
    },
    onFillSelected(templateId) {
      this.startFillFor(
        this.selectedForms.map((f) => f.id),
        templateId || this.scopedTemplateId,
      );
      this.selectedForms = [];
    },
    // Download a single form: the filled output bytes when a successful
    // form-fill record exists, otherwise (never filled or the fill failed) the
    // original uploaded form document, fetched by its opportunity document id.
    downloadForm(form) {
      const fill = this.formFillByDocument(form.id);
      const request =
        fill && form.status !== 'FAILED'
          ? opportunityDocumentsService.getDocumentFormFile({ opportunityId: this.opportunityId, formFillId: fill.id })
          : opportunityDocumentsService.getDocumentFile({ opportunityId: this.opportunityId, documentId: form.id });
      request.then((fileBlob) => downloadBlob(fileBlob, form.name)).catch((error) => this.$toast.error(error));
    },
    onDownloadOne(formId) {
      const form = this.forms.find((f) => f.id === formId);
      if (form) this.downloadForm(form);
    },
    onDownloadAll() {
      if (!this.forms.length) {
        this.$toast.info(this.$t('page.opportunity-forms.actions.download-all.empty'));
        return;
      }
      const count = this.forms.length;
      const zipName = `${(this.opportunity && this.opportunity.name) || 'forms'}.zip`;
      opportunityDocumentsService
        .getAllDocumentFormFiles({ opportunityId: this.opportunityId })
        .then((zipBlob) => {
          downloadBlob(zipBlob, zipName);
          this.$toast.success(this.$tc('page.opportunity-forms.actions.download-all.success', count, { count }));
        })
        .catch((error) => this.$toast.error(error));
    },
    onReview(formId) {
      this.$router.push({
        name: 'opportunity-form-review',
        params: { opportunityId: this.opportunityId, formId },
      });
    },
    onDownloadSelected() {
      if (!this.selectedForms.length) {
        this.$toast.info(this.$t('page.opportunity-forms.actions.download-all.empty'));
        return;
      }
      this.selectedForms.forEach((form) => this.downloadForm(form));
    },
    // A phone deletes one form at a time, from its row, through the same confirmation.
    onConfirmDeleteForm(form) {
      this.selectedForms = [form];
      this.onConfirmDeleteForms();
    },
    onConfirmDeleteForms() {
      if (this.selectedForms.length) this.$refs.deleteFormsDialog.openDialog();
    },
    onDeleteForms() {
      const count = this.selectedForms.length;
      this.deleteForms({ opportunityId: this.opportunityId, formIds: this.selectedForms.map((f) => f.id) })
        .then(() => {
          this.$toast.success(this.$tc('page.opportunity-forms.actions.deleted', count, { count }));
        })
        .catch((error) => this.$toast.error(error));
      this.selectedForms = [];
    },
  },
};
</script>

<style lang="scss">
.opportunity-forms-page {
  min-height: calc(var(--viewport-height) - #{$menu-top-bar-height});
  width: 100%;

  &__content {
    padding: $space-xl;
  }

  &__hidden-input {
    display: none;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-lg;
    margin-bottom: $space-md;
    flex-wrap: wrap;
  }

  &__title {
    font-family: $font-family-heading;
    @include text-style('heading-xl');
    color: $color-text-title;
    margin: 0;
  }

  &__stat {
    @include text-style('body-md');
    color: $color-text-body;
    margin: $space-1 0 0;
    display: flex;
    align-items: center;
    gap: $space-sm;
    flex-wrap: wrap;
    font-variant-numeric: $font-variant-tabular;
  }

  &__filling {
    display: inline-flex;
    align-items: center;
    gap: $space-1;
    color: $color-text-ai;
  }

  &__filling-icon {
    width: 14px;
    height: 14px;
    animation: opportunity-forms-spin 1s linear infinite;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: $space-md;
    flex-wrap: wrap;
  }

  &__delete {
    @include text-style('body-md');
    color: $color-text-body;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: $space-md;
    margin-bottom: $space-md;
    padding: $space-2 $space-3;
    background: $color-surface-raised;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    flex-wrap: wrap;
  }

  &__controls-left {
    display: flex;
    align-items: center;
    gap: $space-md;
    flex: 1;
    min-width: 0;
    flex-wrap: wrap;
  }

  &__scope {
    display: flex;
    align-items: center;
    gap: $space-sm;
    flex-wrap: wrap;
  }

  &__scope-label {
    @include text-style('label-md');
    color: $color-text-muted;
    text-transform: uppercase;
  }

  &__scope-current {
    display: inline-flex;
    align-items: center;
    gap: $space-sm;
    padding: $space-1 $space-2;
    background: $color-surface-sunken;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    cursor: pointer;
    @include text-style('body-md');
    color: $color-text-title;
    transition: border-color $duration-fast $ease-ui-interaction;

    &:hover {
      border-color: $color-accent-300;
    }
  }

  &__scope-icon {
    width: 16px;
    height: 16px;
    color: $color-text-muted;
    flex-shrink: 0;
  }

  &__scope-caret {
    width: 16px;
    height: 16px;
    color: $color-text-muted;
    flex-shrink: 0;
  }

  &__scope-menu {
    background: $color-surface-raised;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    padding: $space-1;
    box-shadow: $shadow-dropdown;
  }

  &__scope-menu-heading {
    @include text-style('label-md');
    color: $color-text-muted;
    text-transform: uppercase;
    padding: $space-1 $space-2;
  }

  &__scope-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-md;
    width: 100%;
    padding: $space-2;
    background: transparent;
    border: none;
    border-radius: $radius-sm;
    cursor: pointer;
    text-align: left;
    transition: background-color $duration-fast $ease-ui-interaction;

    &:hover {
      background: $color-surface-sunken;
    }

    &.is-active {
      background: $color-highlight-pale;
    }
  }

  &__scope-option-name {
    @include text-style('body-md');
    color: $color-text-title;
  }

  &__scope-option-meta {
    display: inline-flex;
    align-items: center;
    gap: $space-1;
    flex-shrink: 0;
  }

  &__divider {
    width: 1px;
    align-self: stretch;
    min-height: 24px;
    background: $color-border;
  }

  &__search {
    flex: 1;
    min-width: 200px;
    max-width: 320px;
  }

  &__filters {
    display: flex;
    gap: $space-sm;
    flex-wrap: wrap;
  }

  &__filter {
    @include text-style('label-md');
    border: 1px solid $color-border;
    background: transparent;
    color: $color-text-body;
    border-radius: $radius-full;
    padding: $space-1 $space-3;
    cursor: pointer;
    transition: all $duration-fast $ease-ui-interaction;

    &.is-active {
      background: $color-highlight-pale;
      border-color: $color-accent-300;
      color: $color-text-action;
    }
  }

  &__fill-progress {
    margin-bottom: $space-md;
    padding: $space-2 $space-3;
    background: $color-surface-raised;
    border: 1px solid $color-border;
    border-radius: $radius-md;
  }

  &__empty {
    display: flex;
    justify-content: center;
    padding-top: $space-2xl;
  }

  // Phones: the screen's gutter, and the template, the search and the filters one under the
  // other, the filters on a line that scrolls sideways.
  @include screen-below('tablet') {
    &__content {
      padding: $phone-page-gutter;
    }

    // Not wrapping: a wrapping column would be as wide as its widest item (the filters' line).
    &__controls,
    &__controls-left {
      flex-direction: column;
      flex-wrap: nowrap;
      align-items: stretch;
      gap: $space-3;
    }

    &__divider {
      display: none;
    }

    &__search {
      min-width: 0;
      max-width: none;
    }

    &__filters {
      flex-wrap: nowrap;
      margin: 0 (-$space-3);
      padding: 0 $space-3;
      overflow-x: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__filter {
      flex: none;
      white-space: nowrap;
    }
  }
}

@keyframes opportunity-forms-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .opportunity-forms-page__filling-icon {
    animation: none;
  }
  .opportunity-forms-page__filter {
    transition-duration: 0ms;
  }
}
</style>
